import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BreakpointMedia, HeapEvent, Route } from 'config';
import { monthDay } from 'utils/dateTimes';
import { useMediaQuery } from 'react-responsive';
import { useCart } from 'contexts/Cart';
import useCurrentUser from 'hooks/useCurrentUser';
import { ValidationRule } from 'models/Cart';

import GiftUserPaymentBanner from 'components/shared/banners/GiftUserPaymentBanner';
import FreeShippingAlert from './alerts/FreeShippingAlert';
import MaximumSelectionAlert from './alerts/MaximumSelectionAlert';
import MaximumMealsAlert from './alerts/MaximumMealsAlert';
import MinimumPriceAlert from './alerts/MinimumPriceAlert';
import MinimumDinnersAlert from './alerts/MinimumDinnersAlert';
import GenericAlert from './alerts/GenericAlert';

import SaveOrDiscardModal from './SaveOrDiscardModal';
import Navigation from './Navigation';
import UpsellingModal from './UpsellingModal';

const VALIDATION_MAPPINGS = {
  [ValidationRule.FREE_SHIPPING]: FreeShippingAlert,
  [ValidationRule.MAXIMUM_SELECTION]: MaximumSelectionAlert,
  [ValidationRule.MAXIMUM_MEALS]: MaximumMealsAlert,
  [ValidationRule.MINIMUM_BASE_PRICE]: MinimumPriceAlert,
  [ValidationRule.MINIMUM_DINNERS]: MinimumDinnersAlert,
};

type Props = {
  filter: string;
  subFilter: string;
  preferenceFilters: string[];
  filteredResultCount: number;
  onFilterClick: (filterArg: string) => void;
  onSubFilterClick: (subFilterArg: string) => void;
  setPreferenceFilters: (preferenceFilters: string[]) => void;
};

function Header({
  filter,
  subFilter,
  preferenceFilters,
  filteredResultCount,
  onFilterClick,
  onSubFilterClick,
  setPreferenceFilters,
}: Props): ReactElement {
  const navigate = useNavigate();
  const {
    cart,
    dispatch,
  } = useCart();
  const { currentUser } = useCurrentUser();
  const {
    credit,
    payment_method: paymentMethod,
  } = currentUser;
  const {
    alert,
    isSummaryVisible,
    menu_id: menuId,
    hasFreeShipping,
  } = cart;

  const hideFreeShippingBanner = alert?.validationRule === ValidationRule.FREE_SHIPPING
    && hasFreeShipping;

  const [addPaymentClicked, setAddPaymentClicked] = useState(false);

  const redirectionURL = `${Route.UPCOMING_MENUS}?cart_updated_id=${menuId}`;

  // Alert component associated to the broken FE validationRule. If there
  // is an alert but still no matching rule, the GenericAlert component
  // is used and the message in the alert is displayed. This is usually
  // the case for alerts generated from BE error responses (they have no
  // broken rule, just an error message).
  const validationRule = alert?.validationRule;
  // @ts-expect-error TODO: fix null safety
  const AlertComponent = VALIDATION_MAPPINGS[validationRule] || GenericAlert;
  const isNarrowScreen = !useMediaQuery({ query: BreakpointMedia.SM });
  const [showSaveOrDiscardModal, setShowSaveOrDiscardModal] = useState(false);
  const [showUpsellingModal, setShowUpsellingModal] = useState(false);
  const [upsellingModalWasSeen, setUpsellingModalWasSeen] = useState(false);
  const [showPreferenceFilterSelection, setShowPreferenceFilterSelection] = useState(false);

  const {
    skip_deadline: skipDeadline,
    delivery_on: deliveryOn,
    subtotalPrice,
    current_week_menu_cart: currentWeekMenuCart,
  } = cart;
  const paymentMethodRequired = (subtotalPrice > credit) && !paymentMethod && currentWeekMenuCart;

  const handleUpcomingDeliveriesClick = (e) => {
    e.preventDefault();

    if (cart.hasChanged && cart.hasError) {
      setShowSaveOrDiscardModal(true);
    } else if (
      !upsellingModalWasSeen
      && cart.productsToUpsell.length
      && cart.hasSelectableProducts
      && !cart.hasError
    ) {
      setShowUpsellingModal(true);
      setUpsellingModalWasSeen(true);
    } else {
      navigate(redirectionURL);
    }
  };

  const handleCloseUpsellingModal = () => {
    setShowUpsellingModal(false);
    navigate(redirectionURL);
  };

  const handleDiscard = () => {
    if (addPaymentClicked) {
      navigate(Route.PROFILE_EDIT_PAYMENT);
    } else {
      navigate(redirectionURL);
    }
  };

  const handleAddPaymentClick = () => {
    setAddPaymentClicked(true);
    if (cart.hasChanged) {
      setShowSaveOrDiscardModal(true);
    } else {
      navigate(Route.PROFILE_EDIT_PAYMENT);
    }
  };

  const hideCartSummary = () => {
    dispatch({
      type: 'toggleSummaryVisible',
      isSummaryVisible: false,
    });
  };
  return (
    <>
      <header
        role="banner"
        className={[
          'c-meal-management-sticky',
          alert ? 'has-validation-alert' : '',
          paymentMethodRequired ? 'has-payment-alert' : '',
        ].join(' ')}
      >
        {showSaveOrDiscardModal && (
          <SaveOrDiscardModal
            onClose={() => setShowSaveOrDiscardModal(false)}
            onDiscard={handleDiscard}
          />
        )}

        {showUpsellingModal && (
          <UpsellingModal
            onClose={handleCloseUpsellingModal}
          />
        )}

        {(alert && !hideFreeShippingBanner) && <AlertComponent cart={cart} />}

        {paymentMethodRequired && (
          <GiftUserPaymentBanner
            centered
            onClick={handleAddPaymentClick}
          />
        )}

        <div
          className="c-meal-management-header"
        >
          <div
            className="l-container"
            style={
              isSummaryVisible && isNarrowScreen
                ? {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }
                : {}
            }
          >
            <div className="c-meal-management-header__container">
              {!(isSummaryVisible && isNarrowScreen) && (
                <div>
                  <div
                    className={[
                      'c-meal-management-header__path',
                      'u-text--sm',
                    ].join(' ')}
                  >
                    <a
                      onClick={handleUpcomingDeliveriesClick}
                      href={redirectionURL}
                      className={HeapEvent.ANCHOR_HOME_PAGE}
                    >
                      Back to Upcoming Deliveries
                    </a>
                    &nbsp;/ Arriving:&nbsp;
                    {monthDay(deliveryOn)}
                  </div>
                  <h1
                    className={[
                      'c-meal-management-header__title',
                      'c-heading',
                      'c-heading--no-margin',
                      'c-heading--brand-6',
                    ].join(' ')}
                  >
                    Customizable until&nbsp;
                    {monthDay(skipDeadline, true)}
                  </h1>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className={[
            'c-meal-management-recipes-shield',
            isSummaryVisible ? null : 'd-none',
          ].join(' ')}
          onClick={hideCartSummary}
          aria-hidden
        />

        <Navigation
          filter={filter}
          subFilter={subFilter}
          preferenceFilters={preferenceFilters}
          filteredResultCount={filteredResultCount}
          showPreferenceFilterSelection={showPreferenceFilterSelection}
          onFilterClick={onFilterClick}
          onSubFilterClick={onSubFilterClick}
          setPreferenceFilters={setPreferenceFilters}
          setShowPreferenceFilterSelection={setShowPreferenceFilterSelection}
        />
      </header>
    </>
  );
}

export default Header;
